<template>
  <app-page
    title="Create an open award"
    subtitle="Set the basics, choose who can earn it, and personalise the award card."
    :previous="[{ label: 'Recognition', to: { name: 'recognition' } }]"
  >
    <template #below-header>
      <p v-if="shouldShowCompanyIndicator" class="company-note">
        This award will be created for <active-company-pill />
      </p>
    </template>

    <template #custom-content>
      <b-col>
        <ValidationObserver v-slot="v">
          <b-form id="create-award-form" @submit.prevent="v.handleSubmit(onSubmit)">
            <div class="form-container">
              <!-- Basic Details -->
              <basic-details-section
                :name="awardForm.name"
                :selected-division-ids="selectedDivisionIds"
                :division-options="divisionOptions"
                @update:name="awardForm.name = $event"
                @update:selectedDivisionIds="onDivisionsChange"
              >
                <template #after-divisions>
                  <hr />

                  <div class="company-values-container">
                    <div class="label-row">
                      <div class="label">
                        Add your company values (optional)
                        <small>
                          Tag relevant values to help employees understand what behaviours this award recognises. You
                          can use our suggestions or customise them to match your organisation's values.
                        </small>
                      </div>
                      <b-form-checkbox v-model="companyValuesEnabled" switch />
                    </div>
                    <div v-if="companyValuesEnabled">
                      <create-company-values-modal v-model="companyValues" />
                    </div>
                  </div>

                  <hr />

                  <div class="short-description-container">
                    <div class="label">
                      Short description (optional)
                      <small>
                        A short description with extra detail about this award — for example its location or budget
                        source.
                      </small>
                    </div>
                    <app-input
                      v-model="shortDescription"
                      type="text"
                      maxlength="500"
                      placeholder="Add a short description (optional)"
                    />
                  </div>
                </template>
              </basic-details-section>

              <!-- Appearance -->
              <image-upload-section
                :show-banner="false"
                :badge-collectible="false"
                :badge-file="badgeFile"
                :badge-preview-url="badgePreviewUrl"
                :allowed-image-types="allowedImageTypes"
                :max-size-bytes="MAX_BANNER_SIZE_BYTES"
                @update:badgeFile="onBadgeFileUpdate"
                @update:badgePreviewUrl="onBadgePreviewUrlUpdate"
                @gallerySelect:badge="onBadgeGallerySelect"
              />
            </div>
          </b-form>
        </ValidationObserver>
      </b-col>
    </template>

    <template #footer>
      <div class="d-flex align-items-center justify-content-between w-100 px-4 py-3">
        <b-button variant="outline-secondary" :disabled="saving" @click="$router.back()">Cancel</b-button>
        <div class="d-flex align-items-center">
          <small v-if="errorMessage" class="text-danger mr-3">{{ errorMessage }}</small>
          <b-button type="submit" variant="primary" :disabled="saving" form="create-award-form">
            <b-spinner v-if="saving" class="mr-2" small />
            {{ saving ? 'Creating...' : 'Create award' }}
          </b-button>
        </div>
      </div>
    </template>
  </app-page>
</template>

<script setup>
import { ValidationObserver } from 'vee-validate';
import { onBeforeMount, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import ApiClient from '@/ApiClient';
import { RECOGNITION_AWARD_TYPES, RECOGNITION_APPROVAL_TYPES } from '@/pages/recognition/constants';
import { useAwardForm } from '@/pages/recognition/composables/useAwardForm';
import AppInput from '@/components/AppInput.vue';
import AppPage from '@/components/AppPage.vue';
import useCompanySwitcher from '@/state/composables/useCompanySwitcher';
import ActiveCompanyPill from '@/pages/recognition/components/ActiveCompanyPill.vue';
import CreateCompanyValuesModal from '@/pages/recognition/components/CreateCompanyValuesModal.vue';
import useToast from '@/composables/useToast';
import BasicDetailsSection from '@/pages/recognition/components/BasicDetailsSection.vue';
import ImageUploadSection from '@/pages/recognition/components/ImageUploadSection.vue';

const { shouldShowCompanyIndicator } = useCompanySwitcher();

// Use shared composable
const {
  awardForm,
  selectedDivisionIds,
  divisionOptions,
  enabledDivisions,
  shortDescription,
  badgeFile,
  badgePreviewUrl,
  allowedImageTypes,
  MAX_BANNER_SIZE_BYTES,
  onBadgeFileUpdate,
  onBadgePreviewUrlUpdate,
  onBadgeGallerySelect,
  saving,
  errorMessage,
  loadDivisions,
  setupCommonWatchers,
  buildBaseProperties,
  getImageData,
} = useAwardForm();

// Open-specific state
const companyValuesEnabled = ref(false);
const companyValues = ref([]);

onBeforeMount(async () => {
  await loadDivisions();
});

const $router = useRouter();
const { doToast, TOAST_VARIANT } = useToast();

// Setup common watchers
setupCommonWatchers();

const buildProperties = () => {
  const props = buildBaseProperties();

  if (companyValuesEnabled.value) {
    const values = (companyValues.value || []).map(v => String(v || '').trim()).filter(Boolean);
    if (values.length) {
      props.company_values = values.map(name => ({ name }));
    }
  }

  return props;
};

// Open awards are non-monetary shout-outs: no amount, no budget, no expiry, and always
// instant, so approval_type is fixed rather than derived from approvers.
const buildPayload = properties => ({
  program_type: RECOGNITION_AWARD_TYPES.OPEN,
  program_name: awardForm.value.name,
  approval_type: RECOGNITION_APPROVAL_TYPES.NONE,
  show_badge: false,
  enabled_divisions: enabledDivisions.value,
  properties,
});

const onSubmit = async () => {
  errorMessage.value = '';
  saving.value = true;

  try {
    const payload = buildPayload(buildProperties());
    const { files, s3Keys } = getImageData();

    await ApiClient.createRecognitionProgram(payload, files, s3Keys);

    doToast('Success', 'Open award created', TOAST_VARIANT.SUCCESS);
    $router.push({ name: 'recognition' });
  } catch (e) {
    errorMessage.value = e?.response?.data?.message || e?.variables?.message || e?.message || 'Failed to create award';
  } finally {
    saving.value = false;
  }
};

const onDivisionsChange = ids => {
  // Open awards have no division-scoped nominator/nominee picker, so nothing is pruned.
  selectedDivisionIds.value = ids;
};
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';
</style>
